import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Loader2, Check, MailWarning, ArrowRight, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [message, setMessage] = useState("");
  const [autoLoggedIn, setAutoLoggedIn] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Link di verifica mancante o non valido.");
      return;
    }
    axios
      .get(`${API}/auth/verify-email`, { params: { token } })
      .then((res) => {
        setStatus("success");
        setMessage(res.data?.message || "Email confermata con successo!");

        // Auto-login: se il backend ha restituito un access_token, salvalo e reindirizza
        if (res.data?.access_token && res.data?.user) {
          try {
            loginWithToken(
              res.data.access_token,
              res.data.user,
              res.data.locali || []
            );
            setAutoLoggedIn(true);
            // Breve delay per mostrare il messaggio di successo, poi redirect
            setTimeout(() => {
              const isAdmin = res.data.user?.ruolo === "admin";
              navigate(isAdmin ? "/" : "/operatore", { replace: true });
            }, 1500);
          } catch (e) {
            // Se loginWithToken fallisce, mostra comunque success ma con CTA verso login
            setAutoLoggedIn(false);
          }
        }
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err?.response?.data?.detail ||
            "Impossibile verificare il link. Potrebbe essere scaduto."
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          "linear-gradient(145deg, #F0EFFE 0%, #F5F5FA 50%, #FFF0F4 100%)",
      }}
      data-testid="verify-email-page"
    >
      <div
        className="absolute top-0 left-0 w-72 h-72 rounded-full opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #6C63FF40 0%, transparent 70%)",
          transform: "translate(-30%, -30%)",
        }}
      />
      <div
        className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #FF658440 0%, transparent 70%)",
          transform: "translate(30%, 30%)",
        }}
      />

      <div
        className="w-full max-w-sm relative z-10 bg-white rounded-[28px] p-8 text-center"
        style={{
          boxShadow:
            "0 8px 40px rgba(108,99,255,0.12), 0 1px 0 rgba(255,255,255,0.8) inset",
        }}
      >
        <img
          src="/yollo-logo.png"
          alt="Yollo"
          className="mx-auto mb-6"
          style={{ width: "100px", height: "auto" }}
        />

        {status === "loading" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-100 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
            </div>
            <h1 className="text-xl font-black text-gray-900 mb-2">
              Verifica in corso...
            </h1>
            <p className="text-sm text-gray-500">
              Stiamo confermando il tuo indirizzo email.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <h1
              className="text-xl font-black text-gray-900 mb-2"
              data-testid="verify-success-title"
            >
              Email confermata!
            </h1>
            <p className="text-sm text-gray-500 mb-6">{message}</p>

            {autoLoggedIn ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                <p
                  className="text-xs text-gray-400 font-semibold"
                  data-testid="auto-login-pending"
                >
                  Ti stiamo portando nella dashboard...
                </p>
              </div>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                className="h-12 w-full text-base font-bold rounded-2xl text-white"
                style={{
                  background: "linear-gradient(135deg, #6C63FF, #8B85FF)",
                  boxShadow: "0 4px 16px rgba(108,99,255,0.35)",
                }}
                data-testid="verify-success-login-btn"
              >
                Accedi ora <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <MailWarning className="h-8 w-8 text-red-500" />
            </div>
            <h1
              className="text-xl font-black text-gray-900 mb-2"
              data-testid="verify-error-title"
            >
              Verifica non riuscita
            </h1>
            <p className="text-sm text-gray-500 mb-6">{message}</p>
            <Button
              onClick={() => navigate("/login")}
              className="h-12 w-full text-base font-bold rounded-2xl text-white"
              style={{
                background: "linear-gradient(135deg, #6C63FF, #8B85FF)",
                boxShadow: "0 4px 16px rgba(108,99,255,0.35)",
              }}
              data-testid="verify-error-login-btn"
            >
              Vai al login <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-xs text-gray-400 mt-4">
              Puoi richiedere un nuovo link dalla pagina di login.
            </p>
          </>
        )}

        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-6 flex items-center gap-1.5 mx-auto text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="verify-back-home"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Torna alla home
        </button>
      </div>
    </div>
  );
};

export default VerifyEmail;
